/**
 * agentService.ts
 *
 * Frontend client for the Phase 2 / slice 1 agent endpoints
 * mounted at `/api/v2/agents` (see server/v2/routes/agents.js).
 *
 * Surface:
 *   - getCapabilitySchema()                published list (drives the
 *                                          checkboxes in the operator UI)
 *   - getAgent(address)                    chain-truth single agent
 *   - getAgentPolicy(address)              combined policy snapshot
 *   - listAgents({ operator })             cached list per operator
 *   - isWhitelisted(agent, contract)       allow-list probe
 *
 *   - buildRegisterTx({...})               returns { xdr, latestLedger }
 *   - buildRevokeTx({...})
 *   - buildSetCapabilitiesTx({...})
 *   - buildPolicy*Tx({...})                bind / set-cap / add-whitelist /
 *                                          remove-whitelist / set-expiry /
 *                                          reset-window
 *   - submitSignedAgentTx(signedXdr, opts) wraps POST /submit and asks the
 *                                          backend to refresh its off-chain
 *                                          index after a confirmed write.
 *
 * High-level convenience helpers (registerAgent, revokeAgent,
 * setAgentCapabilities, ...) are intentionally *not* in this slice
 * yet because they need the operator's signing wallet which differs
 * between Freighter (G…) and passkey-kit (C…). Slice 1c's UI will
 * call into the build/submit primitives directly with the right
 * signer; slice 1d's MCP server will use the passkey-kit path.
 */

import { humanizeSorobanError } from '../utils/sorobanContractErrors';

// ── Types ──────────────────────────────────────────────────────────────────

export type AgentStatusLabel = 'active' | 'revoked' | string;

export interface AgentRecord {
  address: string;
  operator: string | null;
  kind: number;
  kindLabel: string;          // "generic" | "trader" | "guide" | "pilot" | …
  capabilities: string[];     // capability ids (matches schema.id)
  status: number;
  statusLabel: AgentStatusLabel;
  createdAtLedger: number;
  updatedAtLedger: number;
  version: number;
}

export interface CachedAgentRecord {
  address: string;
  operator: string;
  kind: number;
  kindLabel: string;
  capabilities: string[];
  status: AgentStatusLabel;
  statusCode?: number;
  createdAtLedger?: number;
  updatedAtLedger?: number;
  version?: number;
  syncedAt?: number;
}

export interface CapabilityEntry {
  id: string;
  label: string;
  description: string;
  category: 'identity' | 'citizenship' | 'movement' | 'broadcast' | string;
  slice: number;
  writes_chain: boolean;
  cost_xlm: number;
}

export interface CapabilitySchema {
  version: number;
  updated_at: string;
  capabilities: CapabilityEntry[];
}

export interface PolicyWindowSnapshot {
  windowStartLedger: number;
  spentInWindow: string;       // i128 returned as string to avoid precision loss
  updatedAtLedger: number;
  version: number;
}

export interface AgentPolicySnapshot {
  configured: boolean;
  agent: string;
  operator?: string | null;
  bound?: boolean;
  expiryLedger?: number | null;
  window?: PolicyWindowSnapshot | null;
  caps?: {
    per_call:   string | null;
    daily_xlm:  string | null;
    tx_per_day: string | null;
    gas_xlm:    string | null;
  };
}

export interface BuiltTx {
  xdr: string;
  latestLedger: number;
}

export interface SubmitResult {
  hash: string;
  ledger?: number;
  agent?: CachedAgentRecord | null;
}

// ── URL resolution ─────────────────────────────────────────────────────────

function backendBaseUrl(): string {
  if (typeof window === 'undefined') return '';
  const isDev =
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1';
  return isDev
    ? `${window.location.protocol}//${window.location.hostname}:8080`
    : '';
}

function url(path: string): string {
  const base = process.env.REACT_APP_BACKEND_URL || backendBaseUrl();
  return `${base}/api/v2/agents${path}`;
}

async function asJson<T>(resp: Response): Promise<T> {
  if (!resp.ok) {
    const txt = await resp.text().catch(() => resp.statusText);
    let msg = txt;
    try {
      const parsed = JSON.parse(txt);
      msg = parsed.error || parsed.message || txt;
    } catch { /* leave as-is */ }
    throw new Error(humanizeSorobanError(msg || `HTTP ${resp.status}`));
  }
  return (await resp.json()) as T;
}

// ── Reads ──────────────────────────────────────────────────────────────────

let _cachedSchema: CapabilitySchema | null = null;
let _cachedSchemaAt = 0;
const SCHEMA_TTL_MS = 5 * 60 * 1000; // capability schema rarely changes

export async function getCapabilitySchema(force = false): Promise<CapabilitySchema> {
  const now = Date.now();
  if (!force && _cachedSchema && now - _cachedSchemaAt < SCHEMA_TTL_MS) {
    return _cachedSchema;
  }
  const resp = await fetch(url('/capabilities/schema'));
  const data = await asJson<CapabilitySchema>(resp);
  _cachedSchema = data;
  _cachedSchemaAt = now;
  return data;
}

export async function getAgent(address: string): Promise<AgentRecord | null> {
  if (!address) return null;
  const resp = await fetch(url(`/${encodeURIComponent(address)}`));
  if (resp.status === 404) return null;
  const data = await asJson<{ success: true; agent: AgentRecord }>(resp);
  return data.agent;
}

/**
 * Chain-truth read that also reconciles the agent into the backend's
 * off-chain operator index, so it shows up in `listAgents` afterwards.
 * Used to recover agents the index missed (post-submit read lag or a
 * sibling instance). Returns null when the address isn't registered.
 */
export async function syncAgent(address: string): Promise<AgentRecord | null> {
  if (!address) return null;
  const resp = await fetch(url(`/${encodeURIComponent(address)}/sync`), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
  if (resp.status === 404) return null;
  const data = await asJson<{ success: true; agent: AgentRecord }>(resp);
  return data.agent;
}

export async function getAgentPolicy(address: string): Promise<AgentPolicySnapshot> {
  if (!address) throw new Error('address is required');
  const resp = await fetch(url(`/${encodeURIComponent(address)}/policy`));
  return await asJson<AgentPolicySnapshot>(resp);
}

export async function listAgents(opts: { operator?: string } = {}): Promise<CachedAgentRecord[]> {
  const params = new URLSearchParams();
  if (opts.operator) params.set('operator', opts.operator);
  const qs = params.toString();
  const resp = await fetch(url(`/${qs ? `?${qs}` : ''}`));
  const data = await asJson<{ count: number; agents: CachedAgentRecord[] }>(resp);
  return data.agents || [];
}

// ── Balances (for the dispose flow) ────────────────────────────────────────

export interface AgentBalanceCredit {
  code:               string;
  issuer:             string;
  balance:            string;
  sellingLiabilities: string;
  buyingLiabilities:  string;
}

export interface AgentBalances {
  address:              string;
  kind:                 'classic' | 'soroban_smart_account';
  unfunded:             boolean;
  native:               { balance: string } | null;
  credits:              AgentBalanceCredit[];
  subentryCount?:       number;
  numSponsoring?:       number;
  numSponsored?:        number;
  baseReserveXlm?:      string;
  hasOpenOffersOrData?: boolean;
  message?:             string;
}

export async function getAgentBalances(address: string): Promise<AgentBalances> {
  if (!address) throw new Error('address is required');
  const resp = await fetch(url(`/${encodeURIComponent(address)}/balances`));
  const data = await asJson<{ success: true } & AgentBalances>(resp);
  return data;
}

export async function isWhitelisted(agent: string, contract: string): Promise<boolean> {
  const resp = await fetch(
    url(`/${encodeURIComponent(agent)}/whitelisted/${encodeURIComponent(contract)}`),
  );
  const data = await asJson<{ allowed: boolean }>(resp);
  return Boolean(data.allowed);
}

/**
 * Cheap "is this wallet an AI agent?" probe.  Returns the active
 * record when registered, `null` otherwise.  Used for the 🤖 badge in
 * profiles, live-feed cards, and map markers — keep it light.
 *
 * Goes through the dedicated `/exists` endpoint (always 200, even
 * for non-agents) instead of `getAgent` (which 404s for unknown
 * addresses). The 404 path was perfectly correct semantically but
 * Chrome auto-logs every failed network response, so the 🤖 probe
 * was filling profile-page consoles with red noise on every render.
 */
export async function probeAgent(address: string): Promise<AgentRecord | null> {
  if (!address) return null;
  try {
    const resp = await fetch(url(`/${encodeURIComponent(address)}/exists`));
    if (!resp.ok) return null;
    const data = await resp.json() as { exists: boolean; agent: AgentRecord | null };
    return data.exists ? data.agent : null;
  } catch {
    // Network blip — treat as "no badge for this render" so the UI
    // doesn't flicker on transient failures.
    return null;
  }
}

// ── Tx builders ────────────────────────────────────────────────────────────

async function postBuild(path: string, body: unknown): Promise<BuiltTx> {
  const resp = await fetch(url(path), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body || {}),
  });
  const data = await asJson<{ success: true; xdr: string; latestLedger: number }>(resp);
  return { xdr: data.xdr, latestLedger: data.latestLedger };
}

export interface RegisterArgs {
  operator: string;
  agent: string;
  kind?: number;            // see AGENT_KIND on the backend (0 generic / 1 trader / 2 guide / 3 pilot)
  capabilities?: string[];
  /**
   * Set when the agent is an EXTERNAL key (e.g. a genAgent G-address)
   * rather than the operator or one of the operator's own smart
   * accounts. The backend uses this to relax its own-account ownership
   * check for the documented MCP onboarding flow.
   */
  external?: boolean;
}

export function buildRegisterTx(args: RegisterArgs): Promise<BuiltTx> {
  const { agent, ...body } = args;
  return postBuild(`/${encodeURIComponent(agent)}/build/register`, body);
}

export function buildRevokeTx(args: { operator: string; agent: string }): Promise<BuiltTx> {
  const { agent, operator } = args;
  return postBuild(`/${encodeURIComponent(agent)}/build/revoke`, { operator });
}

export function buildSetCapabilitiesTx(args: {
  operator: string;
  agent: string;
  capabilities: string[];
}): Promise<BuiltTx> {
  const { agent, ...body } = args;
  return postBuild(`/${encodeURIComponent(agent)}/build/set-capabilities`, body);
}

export function buildPolicyBindTx(args: { operator: string; agent: string }): Promise<BuiltTx> {
  const { agent, operator } = args;
  return postBuild(`/${encodeURIComponent(agent)}/build/policy/bind`, { operator });
}

export function buildPolicySetCapTx(args: {
  operator: string;
  agent: string;
  key: string;
  value: string | number | bigint;
}): Promise<BuiltTx> {
  const { agent, ...body } = args;
  return postBuild(`/${encodeURIComponent(agent)}/build/policy/set-cap`, {
    ...body,
    value: typeof body.value === 'bigint' ? body.value.toString() : body.value,
  });
}

export function buildPolicyAddWhitelistTx(args: {
  operator: string;
  agent: string;
  contract: string;
}): Promise<BuiltTx> {
  const { agent, ...body } = args;
  return postBuild(`/${encodeURIComponent(agent)}/build/policy/add-whitelist`, body);
}

export function buildPolicyRemoveWhitelistTx(args: {
  operator: string;
  agent: string;
  contract: string;
}): Promise<BuiltTx> {
  const { agent, ...body } = args;
  return postBuild(`/${encodeURIComponent(agent)}/build/policy/remove-whitelist`, body);
}

export function buildPolicySetExpiryTx(args: {
  operator: string;
  agent: string;
  ledger: number;
}): Promise<BuiltTx> {
  const { agent, ...body } = args;
  return postBuild(`/${encodeURIComponent(agent)}/build/policy/set-expiry`, body);
}

export function buildPolicyResetWindowTx(args: { operator: string; agent: string }): Promise<BuiltTx> {
  const { agent, operator } = args;
  return postBuild(`/${encodeURIComponent(agent)}/build/policy/reset-window`, { operator });
}

// ── Submit ─────────────────────────────────────────────────────────────────

export interface SubmitOpts {
  /**
   * When set, the backend re-pulls the on-chain record for this
   * agent after the tx confirms and updates its off-chain index, so
   * subsequent `listAgents` calls reflect the new state without a
   * page reload.
   */
  agentToRefresh?: string;
}

export async function submitSignedAgentTx(
  signedXdr: string,
  opts: SubmitOpts = {},
): Promise<SubmitResult> {
  const resp = await fetch(url('/submit'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ signedXdr, agentToRefresh: opts.agentToRefresh }),
  });
  const data = await asJson<{
    success: true;
    hash: string;
    ledger?: number;
    agent?: CachedAgentRecord | null;
  }>(resp);
  return { hash: data.hash, ledger: data.ledger, agent: data.agent ?? null };
}
